// src/components/PageFileDrop.jsx
// 画面のどこにファイルを落としても取り込めるようにする【2026-09-17】
//
//   取り込みがある画面では、ドロップ枠を狙わなくてもよいようにする。
//   ・window で dragenter / dragover / dragleave / drop を受ける
//   ・画面内のドロップ枠（onDrop で preventDefault 済み）が受けた分は二重に扱わない
//   ・ドラッグ中は画面全体に「ここに落とす」を出す（pointer-events: none）
//   ・ファイル以外（文字列・画像のドラッグ）には反応しない
//
//   出席記録ページは AssessmentImportPane が同じことを自前でやっている（フォルダも受ける）。
import React, { useEffect, useRef, useState } from 'react';

/**
 * @param {(files: File[]) => void} onFiles 落とされたファイル
 * @param {{ enabled?: boolean }} opts
 * @returns {boolean} ドラッグ中かどうか
 */
export function usePageFileDrop(onFiles, { enabled = true } = {}) {
  const [dragging, setDragging] = useState(false);
  const cb = useRef(onFiles);
  cb.current = onFiles;

  useEffect(() => {
    if (!enabled) { setDragging(false); return undefined; }
    let depth = 0;
    const hasFiles = (e) =>
      !!e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files');
    const onEnter = (e) => { if (!hasFiles(e)) return; depth += 1; setDragging(true); };
    // これが無いと drop が起きず、ブラウザがファイルを開いて画面から離れてしまう
    const onOver = (e) => { if (hasFiles(e)) e.preventDefault(); };
    const onLeave = (e) => {
      if (!hasFiles(e)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) setDragging(false);
    };
    const onDrop = (e) => {
      if (!hasFiles(e)) return;
      depth = 0;
      setDragging(false);
      if (e.defaultPrevented) return;   // 画面内のドロップ枠が受けた
      e.preventDefault();
      const files = Array.from(e.dataTransfer.files || []);
      if (files.length > 0) cb.current(files);
    };
    window.addEventListener('dragenter', onEnter);
    window.addEventListener('dragover', onOver);
    window.addEventListener('dragleave', onLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onEnter);
      window.removeEventListener('dragover', onOver);
      window.removeEventListener('dragleave', onLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [enabled]);

  return dragging;
}

/**
 * ドラッグ中に画面全体へ出す案内【2026-09-17 見た目を作り直し】
 *   Claude / ChatGPT のように、画面を薄暗くして中央にファイルの絵と説明を出す。
 *   kinds: 受け付ける種類のラベル（ファイルの絵とチップに使う）
 *   pointer-events: none なので、下の画面の操作・ドロップを邪魔しない。
 */
const OVERLAY_CSS = `
@keyframes pfd-fade { from { opacity: 0 } to { opacity: 1 } }
@keyframes pfd-pop  { from { opacity: 0; transform: translateY(8px) scale(.96) } to { opacity: 1; transform: none } }
@keyframes pfd-float { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-6px) } }
.pfd-root { animation: pfd-fade .12s ease-out }
.pfd-panel { animation: pfd-pop .18s cubic-bezier(.2,.8,.3,1.2) }
.pfd-files { animation: pfd-float 1.6s ease-in-out infinite }
@media (prefers-reduced-motion: reduce) {
  .pfd-root, .pfd-panel, .pfd-files { animation: none }
}
`;

const KIND_COLOR = {
  CSV: '#16a34a', XLSX: '#15803d', XLS: '#15803d', PDF: '#dc2626',
  'フォルダ': '#d97706', ZIP: '#7c3aed',
};

function FileCard({ label, color, rotate, dx, dy, z }) {
  return (
    <svg width="64" height="80" viewBox="0 0 64 80"
         style={{ position: 'absolute', left: '50%', top: '50%', zIndex: z,
                  transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) rotate(${rotate}deg)`,
                  filter: 'drop-shadow(0 6px 12px rgba(15,23,42,.25))' }}
         aria-hidden="true">
      <path d="M8 2h34l20 20v50a6 6 0 0 1-6 6H8a6 6 0 0 1-6-6V8a6 6 0 0 1 6-6z" fill="#fff" />
      <path d="M42 2v14a6 6 0 0 0 6 6h14" fill="#e2e8f0" />
      <rect x="10" y="30" width="30" height="4" rx="2" fill="#cbd5e1" />
      <rect x="10" y="39" width="40" height="4" rx="2" fill="#e2e8f0" />
      <rect x="10" y="48" width="24" height="4" rx="2" fill="#e2e8f0" />
      <rect x="8" y="58" width={Math.max(28, label.length * 9 + 12)} height="15" rx="4" fill={color} />
      <text x={8 + Math.max(28, label.length * 9 + 12) / 2} y="69.5" textAnchor="middle"
            fontSize="10" fontWeight="700" fill="#fff" fontFamily="system-ui, sans-serif">{label}</text>
    </svg>
  );
}

export function PageDropOverlay({ show, title = 'ファイルをドロップして取り込む', note = '', kinds = ['CSV', 'XLSX'] }) {
  if (!show) return null;
  // 絵は3枚（左奥・右奥・手前）。手前が一番よく使う種類（kinds の先頭）
  const k0 = kinds[0] || 'CSV';
  const cards = [kinds[1] || k0, kinds[2] || kinds[1] || k0, k0];
  const pose = [
    { rotate: -14, dx: -30, dy: 6, z: 1 },
    { rotate: 12, dx: 30, dy: 6, z: 2 },
    { rotate: 0, dx: 0, dy: -4, z: 3 },
  ];
  return (
    <div className="pfd-root" role="presentation"
         style={{ position: 'fixed', inset: 0, zIndex: 4000, pointerEvents: 'none',
                  background: 'rgba(15,23,42,0.45)',
                  backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <style>{OVERLAY_CSS}</style>
      {/* 画面の縁の破線 */}
      <div aria-hidden="true"
           style={{ position: 'absolute', inset: 14, borderRadius: 18,
                    border: '2.5px dashed rgba(255,255,255,.75)' }} />

      <div className="pfd-panel" style={{ textAlign: 'center', color: '#fff', padding: '0 24px', maxWidth: 560 }}>
        <div style={{ transform: 'scale(1.3)', transformOrigin: 'center bottom', margin: '0 auto 22px', width: 160 }}>
        <div className="pfd-files" style={{ position: 'relative', width: 160, height: 110 }}>
          {cards.map((k, i) => (
            <FileCard key={i} label={k} color={KIND_COLOR[k] || '#2563eb'} {...pose[i]} />
          ))}
          {/* ＋ のバッジ */}
          <div style={{ position: 'absolute', right: 10, bottom: -4, zIndex: 4, width: 30, height: 30,
                        borderRadius: '50%', background: '#2563eb', color: '#fff', fontSize: 20,
                        fontWeight: 700, lineHeight: '30px', boxShadow: '0 4px 10px rgba(37,99,235,.5)' }}>
            ＋
          </div>
        </div>
        </div>
        <div style={{ fontSize: '1.45rem', fontWeight: 800, letterSpacing: '0.02em',
                      textShadow: '0 2px 8px rgba(0,0,0,.25)' }}>
          {title}
        </div>
        {note && (
          <div style={{ fontSize: '0.92rem', marginTop: 8, opacity: 0.92, lineHeight: 1.7 }}>{note}</div>
        )}
        {kinds.length > 0 && (
          <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap', marginTop: 14 }}>
            {kinds.map(k => (
              <span key={k} style={{ fontSize: '0.75rem', fontWeight: 700, padding: '3px 10px', borderRadius: 999,
                                     background: 'rgba(255,255,255,.16)', border: '1px solid rgba(255,255,255,.4)' }}>
                {k}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
